// client/src/services/conversationService.js

import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

/**
 * Obtiene todas las conversaciones desde el backend con paginación.
 * - Lee el token desde localStorage y lo incluye en la cabecera Authorization.
 * - Devuelve un objeto con { conversations, total, limit, offset }
 * - Si el status no es 200, lanza un error con el mensaje JSON recibido o un texto genérico.
 * @param {Object} params - Parámetros de consulta
 * @param {string} [params.whatsappId] - Filtrar por whatsappId específico
 * @param {string} [params.agentId] - Filtrar por agente asignado (o 'unassigned' para sin asignar)
 * @param {boolean} [params.showAll] - Mostrar todas las conversaciones (incluyendo cerradas y nuevas)
 * @param {boolean} [params.noAgentResponse] - Filtrar conversaciones sin respuesta de agente
 * @param {boolean} [params.closed] - Filtrar conversaciones cerradas
 * @param {boolean} [params.newConversations] - Filtrar conversaciones nuevas
 * @param {number} [params.limit=50] - Número de conversaciones a cargar
 * @param {number} [params.offset=0] - Posición desde donde comenzar a cargar
 */
export async function getAllConversations({ whatsappId, agentId, showAll, noAgentResponse, closed, newConversations, messageSearch, limit = 50, offset = 0 } = {}) {
  // 1) Recuperar el token almacenado
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  // 2) Construir query params
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('offset', offset);
  if (whatsappId) {
    params.append('whatsappId', whatsappId);
  }
  if (agentId) {
    params.append('agentId', agentId);
  }
  if (showAll) {
    params.append('showAll', 'true');
  }
  if (noAgentResponse) {
    params.append('noAgentResponse', 'true');
  }
  if (closed) {
    params.append('closed', 'true');
  }
  if (newConversations) {
    params.append('newConversations', 'true');
  }
  if (messageSearch) {
    params.append('messageSearch', messageSearch);
  }

  // 3) Hacer la petición
  const res = await fetch(`${API_URL}/conversations?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  // 4) Si la respuesta no es OK, parsear mensaje de error
  if (!res.ok) {
    let errMsg = 'Error al obtener conversaciones';
    try {
      const data = await res.json();
      errMsg = data.message || errMsg;
    } catch {
      // Ignorar si no viene JSON
    }
    throw new Error(errMsg);
  }

  // 5) Si todo va bien, devolvemos el JSON con el objeto de paginación
  const data = await res.json();
  return data;
}

/**
 * Asigna un agente a una conversación concreta.
 * - conversationId: id de la conversación
 * - agentId: ID del agente obtenido desde localStorage (userID)
 */
export async function assignAgentToConversation(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/assign`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errMsg = 'Error al asignar agente a la conversación';
    try {
      const data = await res.json();
      errMsg = data.message || data.error || errMsg;
    } catch { }
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Reasigna una conversación a un agente específico
 * - conversationId: ID de la conversación
 * - agentId: ID del agente al que se reasignará
 */
export async function reassignConversation(conversationId, agentId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/reassign`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ agentId })
  });

  if (!res.ok) {
    let errMsg = 'Error al reasignar conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch { }
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Marca la conversación como leída.
 * - conversationId: id de la conversación.
 */
export async function markConversationAsRead(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/read`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al marcar conversación como leída';
    try {
      const data = await res.json();
      errMsg = data.message || data.error || errMsg;
    } catch { }
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Marcar la conversación como automática nuevamente.
 * - conversationId: ID de la conversación.
 */
export async function markConversationAsAutomated(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/automate`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errMsg = 'Error al marcar como automática';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch { }
    throw new Error(errMsg);
  }

  return await res.json(); // { message: 'Conversación marcada como automática.' }
}

/**
 * Obtiene los detalles de una conversación (incluido status) por su ID.
 */
export async function getConversationById(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errMsg = 'Error al obtener la conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch { }
    throw new Error(errMsg);
  }

  return await res.json(); // { id, whatsappId, participants, status, lastMessageAt, read }
}

export const handleArchiveConversations = async (archive, ids) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }
  
  const payload = {
    archive: archive,
    ids: ids
  };

  try {
    const res = await fetch(`${API_URL}/conversations/archive`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const { message } = await res.json().catch(() => ({ message: message }));
      return;
    }

    return await res.json();
  } catch {

   }
}

export async function markAllRead() {
  const res = await fetch(`${API_URL}/conversations/readAll`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Error marcando todas como leídas');
  }
  return res.json();
}

/**
 * Crear una nueva conversación manualmente
 * @param {string} whatsappId - Número de teléfono WhatsApp
 * @param {string} [market] - Mercado opcional (si no se proporciona, se usa el del usuario)
 * @returns {Promise<Object>} - Datos de la conversación creada
 */
export async function createNewConversation(whatsappId, market = null) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const payload = { whatsappId };
  if (market) {
    payload.market = market;
  }

  const res = await fetch(`${API_URL}/conversations/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    let errMsg = 'Error al crear conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Actualizar leadId y email de una conversación
 * @param {string} conversationId - ID de la conversación
 * @param {Object} details - Detalles a actualizar
 * @param {string} [details.leadId] - Lead ID
 * @param {string} [details.email] - Email del contacto
 * @returns {Promise<Object>} - Datos actualizados
 */
export async function updateConversationDetails(conversationId, details) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/details`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(details)
  });

  if (!res.ok) {
    let errMsg = 'Error al actualizar detalles';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Obtiene estadísticas de conversaciones sin respuesta de agente
 * @returns {Promise<Object>} - Estadísticas con total, byStatus, byMarket y conversaciones
 */
export async function getNoAgentResponseStats() {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/stats/no-agent-response`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al obtener estadísticas';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Cierra una conversación (cambia status a 'closed')
 * @param {string} conversationId - ID de la conversación a cerrar
 */
export async function closeConversation(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/close`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al cerrar conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Reabre una conversación cerrada
 * @param {string} conversationId - ID de la conversación a reabrir
 */
export async function reopenConversation(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}/reopen`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al reabrir conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Elimina una conversación permanentemente
 * @param {string} conversationId - ID de la conversación a eliminar
 */
export async function deleteConversation(conversationId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al eliminar conversación';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}
